// src/services/approverIdentity.js
//
// Who is signed in, in a form fit to record against an approval.
//
// Approving a source-document template is the moment a study's blank forms
// become the ones every patient is enrolled against, so "who approved this"
// has to survive in the record. The email always exists — it is the identity
// the user authenticated with. A NAME does not: staff accounts are created by
// the createStaffUser Cloud Function, which sets no displayName, and
// employeeCredentials records carry a NAME but no email to join them on.
//
// So this looks in the two places a name can honestly come from and, failing
// both, records none:
//
//   1. the Firebase Auth profile's displayName, if someone has set one
//   2. the `coordinators` record whose `email` matches — the one staff
//      collection that can actually be joined to a signed-in user
//
// It NEVER invents a name or initials from an email address. "kgoel@…" is not
// evidence that anybody is called K G, and a fabricated initial in an approval
// record is worse than an honest blank — the timeline shows the email instead.

import { collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getCoordinatorsPath } from "./firestorePaths";

/**
 * Initials from a REAL name — first letter of the first and last words.
 *
 * An email address is rejected outright rather than left to callers to guard
 * against: "kgoel@lunginnovations.com" would otherwise yield a confident-
 * looking "KC", and a fabricated initial in an approval record is worse than
 * an honest blank. Making that structural means a future call site can't
 * reintroduce the problem by forgetting.
 *
 * @param {string} name
 * @returns {string} initials, or "" if the input isn't a name
 */
export const initialsFromName = (name) => {
  const raw = String(name || "");
  if (raw.includes("@")) return "";
  const words = raw
    .replace(/[^\p{L}\s'-]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "";
  const first = words[0][0] || "";
  const last = words.length > 1 ? words[words.length - 1][0] || "" : "";
  return (first + last).toUpperCase();
};

/**
 * The signed-in user as an audit/approval actor.
 *
 * Never throws and never blocks: a directory lookup that fails just means the
 * approval is recorded against the email alone, which is still a complete
 * answer to "who did this".
 *
 * @returns {Promise<{email: string, uid: string|null, name: string, initials: string}>}
 */
export const resolveCurrentActor = async () => {
  const user = auth.currentUser;
  const email = String(user?.email || "").trim();
  const base = { email, uid: user?.uid || null, name: "", initials: "" };

  const fromProfile = String(user?.displayName || "").trim();
  if (fromProfile) {
    return { ...base, name: fromProfile, initials: initialsFromName(fromProfile) };
  }
  if (!email) return base;

  try {
    const snap = await getDocs(
      query(collection(db, getCoordinatorsPath()), where("email", "==", email))
    );
    const name = String(snap.docs[0]?.data()?.name || "").trim();
    if (name) return { ...base, name, initials: initialsFromName(name) };
  } catch (_) {
    // No directory entry, or the collection isn't readable from here. The
    // email alone still identifies the approver.
  }
  return base;
};

/**
 * How to show an actor in one line: "Kinjal Goel (KG)" when a name is known,
 * otherwise the bare email. Deliberately never "unknown (??)".
 * @param {object} actor
 * @returns {string}
 */
export const describeActor = (actor) => {
  if (!actor) return "unknown";
  const name = String(actor.name || "").trim();
  const initials = String(actor.initials || "").trim();
  if (name) return initials ? `${name} (${initials})` : name;
  return String(actor.email || "unknown");
};
